import type { AwardCriterionDto, AwardSubCriterionDto } from "../../autogen/bff-api";
import { isTiptapContentEmpty } from "../input/TipTap/TipTap";
import {
  criterionStatus,
  subCriterionStatus,
  type CriterionStatus,
  type SubCriterionStatus,
} from "../award-criteria/awardCriteriaStatus";

/**
 * How complete an award criterion is *as a definition* — the library's question, which is not quite the
 * procurement's.
 *
 * A criterion in a procurement is measured against the publish gates: every requirement under it must
 * carry a weight, and the weights must total 100. A criterion in the library is measured against what it
 * will be worth when someone reuses it, and there one state is legitimate that the gates would reject: a
 * set nobody has weighted at all. That is what "add from the library" produces — requirements arrive
 * unweighted, because a share belongs to the competition — and a buyer may deliberately leave the
 * weighting to each procurement.
 *
 * So the rules here are the wizard's, with one exception carved out: an entirely unweighted set is
 * quiet. The moment one requirement carries a weight the set is inconsistent rather than undecided, and
 * the wizard's rules take over. Amber is worth reserving for that, or it stops meaning anything.
 */

/** A set nobody has weighted is a definition, not a half-finished one. */
export const isUnweightedSet = (criterion: AwardCriterionDto): boolean => {
  const subCriteria = criterion.subCriteria ?? [];
  return subCriteria.length > 0 && subCriteria.every((sub) => sub.weight?.number == null);
};

/** The library adds one state of its own; the rest are the wizard's, unchanged. */
export type LibraryCriterionStatus = CriterionStatus | { kind: "unweighted" };

export const libraryCriterionStatus = (criterion: AwardCriterionDto): LibraryCriterionStatus => {
  if (isUnweightedSet(criterion)) {
    // The weights are not being judged, so the description is all that is left to be missing.
    if (isTiptapContentEmpty(criterion.description)) return { kind: "missing-description" };
    return { kind: "unweighted" };
  }
  return criterionStatus(criterion);
};

/** Null where there is nothing worth saying — an unweighted requirement is not thereby "complete". */
export const librarySubCriterionStatus = (
  criterion: AwardCriterionDto,
  sub: AwardSubCriterionDto
): SubCriterionStatus | null => {
  if (isUnweightedSet(criterion)) {
    const described = !!sub.name?.trim() && !isTiptapContentEmpty(sub.description);
    return described ? null : "missing-description";
  }
  return subCriterionStatus(sub);
};
